import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { User, Client, CampaignType, AutocompleteOption } from "../shared/types";

import ClientSelect from "./ClientSelect";

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
} from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { SelectItem } from "@/components/ui/select";
import { FormCheckbox } from "@/components/forms/FormCheckbox";
import { formComboboxInputClassName } from "@/components/forms/FormCombobox";
import { FormFieldShell } from "@/components/forms/FormField";
import { FormInput } from "@/components/forms/FormInput";
import { FormSelect } from "@/components/forms/FormSelect";
import {
  scrollVirtualizedHighlight,
  VirtualizedComboboxList,
} from "@/components/forms/VirtualizedComboboxList";
import { useCreatableComboboxFilter } from "@/components/forms/useCreatableComboboxFilter";
import { cn } from "cn";
import type { Virtualizer } from "@tanstack/react-virtual";

interface BookerSelectProps {
  onBookerSelect: (user: User | undefined) => unknown;
  value: User | undefined;
  bookers: User[];
  clients: Client[];
  disabled?: boolean;
}

interface DialogValue {
  firstName: string;
  lastName: string;
  email: string;
  campaignType: string;
  client: Client | undefined;
}

const initialDialogState: DialogValue = {
  firstName: "",
  lastName: "",
  email: "",
  campaignType: "",
  client: undefined,
};

function generateBookerLabel(user: User) {
  return `${user.email}: ${user.firstName || ""} ${user.lastName || ""} -- ${user.company || ""} -- ${user.id || "New Booker"}`;
}

function BookerSelect(props: BookerSelectProps) {
  const { bookers, disabled } = props;
  const [localValue, setLocalValue] = useState(props.value);

  const bookerOptions: AutocompleteOption<User>[] = useMemo(
    () =>
      bookers
        .filter((booker) => !booker.hideBooker)
        .map((booker) => ({
          val: booker,
          label: generateBookerLabel(booker),
        })),
    [bookers]
  );

  const visibleClients = useMemo(
    () => props.clients.filter((client) => !client.hidden),
    [props.clients]
  );

  const [dialogValue, setDialogValue] = useState<DialogValue>(initialDialogState);
  const [dialogOpen, toggleDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string>();
  const [confirmedClient, setConfirmedClient] = useState(false);
  const [open, setOpen] = useState(false);
  const virtualizerRef = useRef<Virtualizer<HTMLDivElement, Element> | null>(
    null
  );

  const selected = useMemo(
    () =>
      localValue
        ? { val: localValue, label: generateBookerLabel(localValue) }
        : null,
    [localValue]
  );

  const committedLabel = selected?.label ?? "";
  const committedLabelRef = useRef(committedLabel);
  committedLabelRef.current = committedLabel;

  const [inputValue, setInputValue] = useState(committedLabel);

  useEffect(() => {
    if (!open) setInputValue(committedLabel);
  }, [committedLabel, open]);

  useEffect(() => {
    setLocalValue(props.value);
  }, [props.value]);

  function commitBooker(booker: User | undefined) {
    setLocalValue(booker);
    props.onBookerSelect(booker);
  }

  const createOption = useCallback(
    (inputValue: string): AutocompleteOption<User> => ({
      val: undefined,
      inputValue,
      label: `Add New Booker "${inputValue}"`,
    }),
    []
  );

  const { filteredItems } = useCreatableComboboxFilter(
    bookerOptions,
    inputValue,
    createOption
  );

  const itemToStringLabel = useCallback(
    (option: AutocompleteOption<User>) => option.label,
    []
  );

  const isItemEqualToValue = useCallback(
    (a: AutocompleteOption<User>, b: AutocompleteOption<User>) =>
      (a.val?.id ?? a.label) === (b.val?.id ?? b.label),
    []
  );

  function onBookerChange(newValue: AutocompleteOption<User> | null) {
    if (newValue && newValue.inputValue) {
      toggleDialogOpen(true);
    } else if (newValue === null) {
      commitBooker(undefined);
    } else {
      commitBooker(newValue.val);
    }
  }

  function resetDialog() {
    toggleDialogOpen(false);
    setDialogValue(initialDialogState);
    setDialogError("");
    setConfirmedClient(false);
  }

  const handleBookerDialogSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const allBookerEmails = bookers.map((booker) =>
      booker.email?.toLowerCase().trim()
    );

    if (!dialogValue.campaignType) {
      setDialogError("Please choose a campaign type.");
    } else if (!dialogValue.client?.name) {
      setDialogError("Please select a client.");
    } else if (!confirmedClient) {
      setDialogError("Please confirm you have selected the correct client.");
    } else if (allBookerEmails.includes(dialogValue.email.toLowerCase().trim())) {
      setDialogError(
        "A booker with this email already exists. Please select existing booker."
      );
    } else {
      const newBooker = {
        ...dialogValue,
        campaignType: dialogValue.campaignType as CampaignType,
        company: dialogValue.client!.name,
        ...(dialogValue.client?.id && { clientId: dialogValue.client.id }),
        ...(dialogValue.client?.airtableId && {
          airtableId: dialogValue.client.airtableId,
        }),
      };
      delete (newBooker as { client?: Client }).client;

      commitBooker(newBooker);
      resetDialog();
    }
  };

  function onClientChange(newValue: string | AutocompleteOption<Client> | null) {
    if (typeof newValue === "string") {
      return;
    }
    if (newValue && newValue.inputValue) {
      setDialogValue({
        ...dialogValue,
        client: {
          name: newValue.inputValue,
        },
      });
    } else if (newValue === null) {
      setDialogValue({
        ...dialogValue,
        client: undefined,
      });
    } else {
      setDialogValue({
        ...dialogValue,
        client: newValue?.val,
      });
    }
  }

  if (!bookerOptions) {
    return null;
  }

  return (
    <>
      {/*
        Close policy (same as ClientSelect / CampaignSelect / clearOnBlur):
        blur reverts filter text. "Add New Booker" only via the creatable row
        (opens the dialog); leftover query text must not replace the selection.
      */}
      <Combobox
        id="booker-select"
        virtualized
        items={bookerOptions}
        filteredItems={filteredItems}
        filter={null}
        value={selected}
        inputValue={inputValue}
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (nextOpen) return;
          // clearOnBlur: discard filter text; creatable commits only via row/dialog.
          setInputValue(committedLabelRef.current);
        }}
        onValueChange={(next) => {
          if (next?.inputValue) {
            // Opens dialog; keep showing the prior committed booker in the field.
            onBookerChange(next);
            setInputValue(committedLabelRef.current);
            return;
          }
          if (next == null) {
            committedLabelRef.current = "";
            setInputValue("");
            onBookerChange(null);
            return;
          }
          committedLabelRef.current = next.label;
          setInputValue(next.label);
          onBookerChange(next);
        }}
        onInputValueChange={setInputValue}
        onItemHighlighted={(_item, { reason, index }) => {
          scrollVirtualizedHighlight(virtualizerRef.current, index, reason);
        }}
        disabled={disabled}
        autoHighlight
        autoComplete="new-password"
        itemToStringLabel={itemToStringLabel}
        isItemEqualToValue={isItemEqualToValue}
      >
        <ComboboxInput
          disabled={disabled}
          showClear={!disabled}
          className={cn(formComboboxInputClassName)}
        />
        {/* Positioner is abs-pos, so anchor-size() can read the toolbar width. Popup fills that. */}
        <ComboboxContent
          positionerClassName="[position-anchor:--talent-browse-toolbar] min-w-[min(anchor-size(width,36rem),var(--available-width))]"
          className="w-full min-w-0"
        >
          <ComboboxEmpty>No bookers found.</ComboboxEmpty>
          <VirtualizedComboboxList<AutocompleteOption<User>>
            open={open}
            estimateSize={36}
            virtualizerRef={virtualizerRef}
            getItemKey={(option) => option.val?.id ?? option.label}
            itemClassName="min-w-0"
          >
            {(option) => <span className="min-w-0 flex-1 truncate">{option.label}</span>}
          </VirtualizedComboboxList>
        </ComboboxContent>
      </Combobox>

      {dialogOpen ? (
        <Dialog
          open={dialogOpen}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) resetDialog();
          }}
        >
          <DialogContent
            className="flex max-h-[min(90vh,720px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg"
            showCloseButton={false}
          >
            <form
              onSubmit={handleBookerDialogSubmit}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <DialogHeader>
                  <DialogTitle>Add a new booker</DialogTitle>
                  <DialogDescription>
                    New Booker? Add their details below!
                  </DialogDescription>
                </DialogHeader>

                <div className="mt-4 space-y-4">
                  <FormInput
                    label="First Name"
                    id="firstName"
                    value={dialogValue.firstName}
                    onChange={(event) =>
                      setDialogValue({
                        ...dialogValue,
                        firstName: event.target.value,
                      })
                    }
                    type="text"
                    required
                  />
                  <FormInput
                    label="Last Name"
                    id="lastName"
                    value={dialogValue.lastName}
                    onChange={(event) =>
                      setDialogValue({
                        ...dialogValue,
                        lastName: event.target.value,
                      })
                    }
                    type="text"
                    required
                  />
                  <FormInput
                    label="Email"
                    id="email"
                    value={dialogValue.email}
                    onChange={(event) =>
                      setDialogValue({
                        ...dialogValue,
                        email: event.target.value,
                      })
                    }
                    type="email"
                    required
                  />
                  <FormSelect
                    label="Campaign Type"
                    id="campaignType"
                    required
                    placeholder="Choose Type..."
                    value={dialogValue.campaignType || null}
                    onChange={(value) =>
                      setDialogValue({
                        ...dialogValue,
                        campaignType: value ?? "",
                      })
                    }
                  >
                    <SelectItem value="Song">Song Promos</SelectItem>
                    <SelectItem value="Brand">Brand Promos</SelectItem>
                  </FormSelect>
                  <FormFieldShell
                    label="Client"
                    required
                    htmlFor="client-select"
                  >
                    {() => (
                      <ClientSelect
                        clients={visibleClients}
                        value={dialogValue.client}
                        onChange={(_event, nextValue) => {
                          onClientChange(nextValue);
                        }}
                      />
                    )}
                  </FormFieldShell>
                  <FormCheckbox
                    id="confirmedClient"
                    required
                    checked={confirmedClient}
                    onChange={setConfirmedClient}
                    label="I have confirmed this is the correct client."
                    description={
                      <>
                        Please confirm you have checked that this is the correct
                        client to link this booker to —{" "}
                        <strong>
                          many clients have multiple international entities
                          (e.g. US &amp; UK)
                        </strong>
                        , so please ensure you have selected the right one.
                        <span className="mt-2 block italic">
                          If you are unsure please confirm with the booker
                          directly before logging the deal, or failing that speak
                          to one of the grail team.
                        </span>
                      </>
                    }
                  />
                  {dialogError ? <FieldError>{dialogError}</FieldError> : null}
                </div>
              </div>

              <DialogFooter className="mx-0 mb-0 shrink-0 border-0 bg-transparent p-4 sm:justify-end">
                <Button type="button" variant="outline" onClick={resetDialog}>
                  Cancel
                </Button>
                <Button type="submit">Add</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

export default BookerSelect;
